// React dependencies
import React, { useState, useMemo } from 'react';

// External dependencies
import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';

// Internal dependencies
import MText from '../../../../../components/Common/MText';
import { IsEmpty } from '../../../../../common/checks';
import MTextField from '../../../../../components/MTextField';
import MapLocationSelect from './MapLocationSelect';
import PoiMarkerIcon from './PoiMarkerIcon';
import PickPoiLocationModal from './PickPoiLocationModal';
import AddressSearchModal from './AddressSearchModal';

const useStyles = makeStyles((theme) => ({
  root: {
    width:                        '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
}));

const PoiLocation = (props) => {
  const classes = useStyles();
  const [showAddressSearchModal, setShowAddressSearchModal] = useState(false);
  const [showSelectLocInMapModal, setShowSelectLocInMapModal] = useState(false);
  const [isModifyingAddressCoordinate, setIsModifyingAddressCoordinate] = useState(false);

  const locationValue = useMemo(() => (
    props.isCustomAddress
      ? 'User selected location'
      : !IsEmpty(props.address)
        ? `${!IsEmpty(props.address?.name) ? props.address.name : 'User selected location'}${
          !IsEmpty(props.address.name) && !IsEmpty(props.address?.postalCode) ? ` ${props.address.postalCode}` : ''}`
        : ''
  ), [props.isCustomAddress, props.address]);

  const onMapClicked = () => {
    setShowSelectLocInMapModal(true);
  };

  const closeAddressSearchModal = () => {
    setShowAddressSearchModal(false);
  };

  return (
    <div className="d-flex flex-column align-items-center mx-auto w-100">
      {!props.disabled && (
        <MText
          text="Where is it located?"
          className="pb-1"
          size={16}
          style={{ fontWeight: 500 }}
        />
      )}
      <MTextField
        className={`mt-1 mb-1 ${classes.root}`}
        disabled={props.disabled}
        readOnly
        placeholder={!props.disabled ? 'Enter address' : ''}
        style={{ fontStyle: (IsEmpty(locationValue) ? 'italic' : 'normal') }}
        value={locationValue}
        onClick={() => (props.disabled ? {} : setShowAddressSearchModal(true))}
        multiLine={false}
      />

      <MapLocationSelect
        className="mt-2"
        GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
        currentCoord={props.address}
        markerIcon={PoiMarkerIcon}
        height="30dvh"
        displayOnly
        disabled={props.disabled}
        onClick={onMapClicked}
      />

      <AddressSearchModal
        open={showAddressSearchModal}
        onClose={closeAddressSearchModal}
        onSuggestedItemSelect={(newAddress) => {
          props.onAddressChange(newAddress);
          props.onIsCustomAddressChange(false);
          closeAddressSearchModal();
        }}
        onOpenMapModal={() => {
          closeAddressSearchModal();
          setShowSelectLocInMapModal(true);
        }}
      />

      <PickPoiLocationModal
        GOOGLE_MAP_API_KEY={process.env.REACT_APP_GOOGLE_API_KEY}
        markerIcon={PoiMarkerIcon}
        onClose={() => {
          setShowSelectLocInMapModal(false);
          setIsModifyingAddressCoordinate(false);
        }}
        open={showSelectLocInMapModal}
        existingMarker={props.address}
        onLocationChange={(location) => {
          if (!IsEmpty(location)) {
            if (isModifyingAddressCoordinate) {
              props.onAddressChange((prev) => ({
                ...prev,
                lat: location.lat,
                lng: location.lng,
              }));
            } else {
              props.onIsCustomAddressChange(true);
              props.onAddressChange({
                lat: location.lat,
                lng: location.lng,
              });
            }
          } else {
            props.onAddressChange(null);
            props.onIsCustomAddressChange(false);
          }
        }}
      />
    </div>
  );
};

PoiLocation.defaultProps = {
  disabled: false,
};

PoiLocation.propTypes = {
  disabled: PropTypes.bool,
  address:  PropTypes.shape({
    name:       PropTypes.string,
    postalCode: PropTypes.number,
  }).isRequired,
  onAddressChange:         PropTypes.func.isRequired,
  isCustomAddress:         PropTypes.bool.isRequired,
  onIsCustomAddressChange: PropTypes.func.isRequired,
};

export default PoiLocation;
